<template>
    <div class="container">
        <div>
            <h1>Oliver's Framework for Blended and Online Instruction</h1>
        </div>
        <div>
            <h2>Introduction</h2>
        </div>
        <div class="content-section">
            Blended and online learning are increasing at astronomical rates as pedagogies for delivering instruction. When teaching in the blended and online environments there are additional areas of focus to consider beyond the traditional teaching environment. Dynamics of classroom management change. Even basic educational theories are applied differently, such as Maslow’s Hierarchy of Needs (Maslow, 1970). Implementing the seamless integration of technology, therefore, creates a different dynamic for effective instruction. As a result, traditional practices for instruction and expectations need to be modified.
        </div>
        <div class="content-section">
            In national focus groups conducted by Oliver (2013, 2014) reoccurring and consistent themes were present. Teachers felt intense pressure from administrators and educational experts to teach using digital strategies. They also had motivation to use this pedagogy to instruct students. However, since the field is still young in research and practice, teachers found few resources comparable to traditional instruction defining “best practices” in the mediums of blended and online instruction. When they did find solid research, administrators and teachers alike, found professional development resources to be limited.
        </div>
        <div class="content-section">
            Simultaneously, as the digital movement progresses, educator accountability intensifies. Understandably, regardless of the benefits for students, teachers are reluctant to try something new, with limited guidance and resources, when their own performance evaluations may be impacted by the results of teaching with a new pedagogy. Administrators openly shared they do not know what ideal practices to observe when completing evaluations. A blended classroom, for example, may look very different than the traditional classroom of the 50’s. They do know, however, that digital technologies engage and motivate students, which equate to increased opportunities for cognitive growth. Oliver’s distinctive Frameworks for Blended and Online Instruction are aligned to national guidelines for identifying and improving blended and online practices.
        </div>
        <div class="content-section">
            Oliver’s Frameworks provide guided examples for the instructor or administrator to utilize as a tool for growth and self-awareness. Further, they provide clear metrics within each dimension pertaining to pedagogical proficiency. It should be stated the frameworks place no inherent value on any scoring within the guided examples; Oliver recognizes some courses and grades, by their very nature, may be best taught fully face-to-face, blended or totally online and these decisions are dependent on student and local education agency (LEA) needs as well as local and state policy.
        </div>
        <div class="content-section">
            Oliver’s Frameworks for Instruction in the Blended and Online Environments are designed to provide best practices to instructors who are implementing digital technology in the classroom and to give administrators guidance in examining resources and determining expectations. It is essential to note that the frameworks and corresponding guided examples are designed to assess blended and online pedagogy, not necessarily blended and online programs. For example, an online program may require that students work in a computer lab face-to-face (f2f) once weekly. Even if a facilitator is present, the instructor of record is still online and responsible for online pedagogy. In the blended framework, the design is for f2f classrooms, where teachers are integrating technology in a blended format in order to support learning as a form of pedagogy. The frameworks are not intended to evaluate programs for accreditation or recommend for Request For Proposal (RFP) evaluations, for example.
        </div>
        <div class="content-section">
            The blended innovation configuration map offers different scenarios for what blended learning should look like, e.g. simulations in classrooms, flipped classroom examples, individualized remediation on the teacher’s website for homework after in class blended lessons. The IC Map is aligned to national professional and student standards that are relevant to blended learning such as ISTE Teacher standards, ISTE student standards, SREB Professional Development Standards, and iNACOL Quality Standards. As well, contextual standards, which are provided in instructional examples that correlate with Oliver’s IC map represent standards from a variety of national organizations to include: Common Core State Standards (CCSSO),National Council of Teachers of Math (NCTM), and Next Generation Science Standards (NGSS).
        </div>
        <div class="content-section">
            For the purposes of this framework, blended is the standard by which a digital classroom should be designed. A classroom in which the teacher replaces an overhead with a boxlight projector is not a blended classroom. It is, rather, a classroom where a teacher integrates technology equipment. In other words, a blended classroom is not one in which the teacher uses a PowerPoint to display notes. A blended classroom is a fully integrated environment, where students are engaged with technology for learning to prepare them for a 21st century global environment.
        </div>
        <div class="content-section">
            In reality blended instruction is comprised of similar practices to how Robert Marzano (2001) and Charlotte Danielson (2007) define an excellent traditional classroom. Albeit with pedagogy changes to include modifications for technology integration with allocations for the larger roles for universal design, digital considerations and instructional design in order to prepare global citizens.
        </div>
        <div>
            <h2>Frameworks</h2>
        </div>
        <div class="content-section">
            Oliver’s Frameworks are divided into six dimensions that are referred to as Domains: Instruction, Professional Responsibilities, Technology, Planning and Preparation, Curriculum, and Instructional Design.
        </div>
        <div style="text-align:left; font-weight: 600; padding-left: 10%;">
            Domains:
        </div>
        <div>
            <ul>
                <li style="color: #c2c2c2; font-weight:600;">DOMAIN 1: Instruction</li>
                <li style="color: #fbd4b4; font-weight: 600;">DOMAIN 2: Professional Responsibilities</li>
                <li style="color: #8fac43; font-weight: 600;">DOMAIN 3: Technology</li>
                <li style="color: #8064a2; font-weight: 600;">DOMAIN 4: Planning & Preparation</li>
                <li style="color: #ffccff; font-weight: 600;">DOMAIN 5: Curriculum</li>
                <li style="color: #d99594; font-weight: 600;">DOMAIN 6: Design</li>
            </ul>
        </div>
        
    </div>
</template>

<script>
export default {
    "name": "framework-section-2",
    setup() {
        
    },
}
</script>

<style lang="scss" scoped>
@import '@/styles/shared.scss';

.container{
    
    h1{
        font-size: 1.0rem;
        text-transform: uppercase;
        color: #662d91;
        font-weight: 800;
        @media(min-width: $bp-tablet-portrait){
            font-size: 1.2rem;
        }
    }

    h2{
        font-size: .9rem;
        @media(min-width: $bp-tablet-portrait){
            font-size: 1rem;
        }
    }

    .content-section{
        padding-left: 5%;
        padding-right: 5%;
        text-align: justify;
        padding-bottom: 5%;
        font-weight: 500;
        font-size: .9rem;

        @media(min-width: $bp-tablet-portrait){
            font-size: 1rem;
            padding-left: 10%;
            padding-right: 10%;
            padding-bottom: 2%;
        }
    }
}

ul{
    text-align: left;
    padding-left: 15%;
}
</style>